import { useCallback, useMemo, useRef, useSyncExternalStore } from "react"

type Cache = Record<string, string>
export type ImageCacheType =
    | "AssetsThumbnail"
    | "PDFThumbnail"
    | "IconImage"
    | "ProfilePicture"
    | "Aircraft"
    | "ProjectThumbnail"

const imageCaches: Record<ImageCacheType, Cache> = {
    AssetsThumbnail: {},
    PDFThumbnail: {},
    IconImage: {},
    ProfilePicture: {},
    Aircraft: {},
    ProjectThumbnail: {},
}
// Used for clearing cache
const maxImages: Record<ImageCacheType, number> = {
    AssetsThumbnail: 20,
    PDFThumbnail: 20,
    IconImage: 50,
    ProfilePicture: 30,
    Aircraft: 50,
    ProjectThumbnail: 50,
}
const imageQueues: Record<ImageCacheType, string[]> = {
    AssetsThumbnail: [],
    PDFThumbnail: [],
    IconImage: [],
    ProfilePicture: [],
    Aircraft: [],
    ProjectThumbnail: [],
}

type ImageState = { blobUrl: string; isFetching: boolean; isError: boolean }

const pending = new Map<string, Promise<void>>()
const failed = new Set<string>()
const subscribers = new Map<string, Set<() => void>>()
const snapshots = new Map<string, ImageState>()

const subscribe = (cacheId: string) => (onChange: () => void) => {
    const listeners = subscribers.get(cacheId) ?? new Set()
    listeners.add(onChange)
    subscribers.set(cacheId, listeners)
    return () => {
        listeners.delete(onChange)
        if (!listeners.size) subscribers.delete(cacheId)
    }
}

const getSnapshot = (type: ImageCacheType, imageKey: string) => () => {
    const cacheId = `${type}:${imageKey}`
    const next = {
        blobUrl: getCachedBlobUrl(type, imageKey) ?? "",
        isFetching: pending.has(cacheId),
        isError: failed.has(cacheId),
    }
    const previous = snapshots.get(cacheId)
    if (
        previous &&
        previous.blobUrl === next.blobUrl &&
        previous.isFetching === next.isFetching &&
        previous.isError === next.isError
    ) {
        return previous
    }
    snapshots.set(cacheId, next)
    return next
}

const notify = (cacheId: string) => subscribers.get(cacheId)?.forEach((onChange) => onChange())

export function addBlobToCache({
    type,
    imageKey,
    imageBlob,
}: {
    type: ImageCacheType
    imageKey: string
    imageBlob: Blob
}) {
    const imageUrl = URL.createObjectURL(imageBlob)
    const img = new Image()
    img.src = imageUrl
    img.decode().catch(() => {})
    imageCaches[type][imageKey] = imageUrl
    if (imageQueues[type].includes(imageKey)) {
        const index = imageQueues[type].indexOf(imageKey)
        imageQueues[type].splice(index, 1)
    }
    imageQueues[type].push(imageKey)
    if (imageQueues[type].length > maxImages[type]) {
        const imageKeyToRemove = imageQueues[type].shift()!
        const imageUrl = imageCaches[type][imageKeyToRemove]
        URL.revokeObjectURL(imageUrl)
        delete imageCaches[type][imageKeyToRemove]
        notify(`${type}:${imageKeyToRemove}`)
    }
    notify(`${type}:${imageKey}`)
    return imageUrl
}

export const getCachedBlobUrl = (type: ImageCacheType, imageKey: string) =>
    imageCaches[type][imageKey] ?? null

export function clearImageFromCache({
    type,
    imageKey,
}: {
    type: ImageCacheType
    imageKey: string
}) {
    const imageUrl = imageCaches[type][imageKey]
    if (!imageUrl) return
    URL.revokeObjectURL(imageUrl)
    delete imageCaches[type][imageKey]
    const index = imageQueues[type].indexOf(imageKey)
    if (index !== -1) imageQueues[type].splice(index, 1)
    failed.delete(`${type}:${imageKey}`)
    notify(`${type}:${imageKey}`)
}

export function useCachedImage({
    type,
    imageKey,
    fetchBlob,
}: {
    type: ImageCacheType
    imageKey: string
    fetchBlob: () => Promise<Blob | undefined>
}) {
    const cacheId = `${type}:${imageKey}`
    const latestFetchBlob = useRef(fetchBlob)
    latestFetchBlob.current = fetchBlob

    const { blobUrl, isFetching, isError } = useSyncExternalStore(
        useMemo(() => subscribe(cacheId), [cacheId]),
        useMemo(() => getSnapshot(type, imageKey), [type, imageKey])
    )

    const fetchBlobUrl = useCallback(() => {
        if (getCachedBlobUrl(type, imageKey) || pending.has(cacheId) || failed.has(cacheId)) return
        const request = latestFetchBlob
            .current()
            .then((blob) => {
                if (blob) addBlobToCache({ type, imageKey, imageBlob: blob })
            })
            .catch(() => {
                failed.add(cacheId)
            })
            .finally(() => {
                pending.delete(cacheId)
                notify(cacheId)
            })
        pending.set(cacheId, request)
        notify(cacheId)
    }, [cacheId, imageKey, type])

    return {
        blobUrl,
        isFetching,
        isError,
        fetchBlobUrl,
    }
}
